import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  ArrowLeft, ArrowRight, FileWarning, IdCard, Loader2, Lock, ScanFace, ShieldAlert, ShieldCheck,
} from "lucide-react";

export const IdVerificationStep = ({
  checklist, ob, facePhotoCount, verifyingId, idFileRef, onUploadIdDoc, setStep,
}) => {
  const idStatus = checklist.id_status;
  const attemptsLeft = checklist.id_attempts_max - checklist.id_attempts;
  return (
    <Card data-testid="onboarding-id-step" className="mt-6 rounded-2xl">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <IdCard className="h-4 w-4" /> Government ID verification
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {idStatus === "verified" ? (
          <div data-testid="onboarding-id-status-verified" className="flex items-start gap-3 rounded-xl border-2 border-[hsl(174,38%,70%)] bg-[hsl(174,38%,96%)] p-4 text-sm">
            <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-[hsl(174,38%,34%)]" />
            <div>
              <p className="font-semibold text-[hsl(174,45%,18%)]">Identity verified</p>
              <p className="text-muted-foreground">
                Your government ID was validated and its portrait matched your dataset photos
                {ob?.id_verification?.confidence ? ` with ${Math.round(ob.id_verification.confidence * 100)}% confidence` : ""}.
                Your ID document is stored privately and is never shown to anyone.
              </p>
            </div>
          </div>
        ) : idStatus === "manual_review" ? (
          <div data-testid="onboarding-id-status-manual-review" className="flex items-start gap-3 rounded-xl border-2 border-[hsl(43,74%,70%)] bg-[hsl(43,74%,95%)] p-4 text-sm">
            <FileWarning className="mt-0.5 h-5 w-5 shrink-0 text-[hsl(43,60%,30%)]" />
            <div>
              <p className="font-semibold text-[hsl(43,60%,22%)]">Under manual review</p>
              <p className="text-[hsl(43,50%,28%)]">
                Automatic verification didn't pass after {checklist.id_attempts_max} attempts, so our trust
                team will review your submission personally. You can't submit more documents right now —
                we'll notify you once it's resolved. Your profile stays hidden in the meantime.
              </p>
            </div>
          </div>
        ) : (
          <>
            <p className="text-sm text-muted-foreground">
              Upload a clear photo of your <span className="font-semibold text-foreground">passport, driver's
              licence, or national ID card</span>. Our AI checks it's a genuine government document and matches
              its portrait to your dataset photos — this blocks stolen identities and deepfakes before they
              ever reach the marketplace.
            </p>
            {idStatus === "rejected" && (
              <div data-testid="onboarding-id-status-rejected" className="flex items-start gap-3 rounded-xl border border-[hsl(6,62%,72%)] bg-[hsl(6,62%,95%)] p-4 text-sm">
                <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
                <div>
                  <p className="font-semibold text-[hsl(6,55%,25%)]">Verification did not pass</p>
                  <p className="text-[hsl(6,40%,30%)]">{ob?.id_verification?.reasoning}</p>
                  <p className="mt-1 text-xs font-medium text-muted-foreground">
                    {attemptsLeft} attempt{attemptsLeft === 1 ? "" : "s"} remaining before manual review is required.
                  </p>
                </div>
              </div>
            )}
            {facePhotoCount < 1 ? (
              <div className="rounded-xl border border-dashed p-4 text-sm text-muted-foreground">
                <Lock className="mb-1 h-4 w-4" /> Upload your front face photo in Step 1 first — the AI needs
                it to match against your ID portrait.
                <Button variant="outline" size="sm" className="ml-3 rounded-lg border bg-card text-xs hover:bg-muted" onClick={() => setStep(1)}>
                  Back to photos
                </Button>
              </div>
            ) : (
              <Button
                type="button"
                data-testid="onboarding-id-upload-button"
                disabled={verifyingId}
                onClick={() => idFileRef.current?.click()}
                className="rounded-xl"
              >
                {verifyingId ? (
                  <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> AI is scanning your ID… (15–45s)</>
                ) : (
                  <><ScanFace className="mr-2 h-4 w-4" /> {idStatus === "rejected" ? "Retry with a new ID photo" : "Upload ID & run AI scan"}</>
                )}
              </Button>
            )}
            <input ref={idFileRef} data-testid="onboarding-id-file-input" type="file" accept="image/jpeg,image/png,image/webp" hidden
              onChange={(e) => onUploadIdDoc(e.target.files)} />
            <p className="text-[11px] text-muted-foreground">
              Attempts used: {checklist.id_attempts}/{checklist.id_attempts_max}. Your ID is encrypted in private
              storage, used only for verification, and never displayed publicly or shared with clients.
            </p>
          </>
        )}
        <div className="flex justify-between border-t pt-4">
          <Button variant="ghost" className="rounded-xl" onClick={() => setStep(1)}>
            <ArrowLeft className="mr-1.5 h-4 w-4" /> Photos
          </Button>
          <Button data-testid="onboarding-id-continue-button" className="rounded-xl" disabled={!checklist.dataset_done}
            onClick={() => setStep(3)}>
            Continue <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
